import { Table, Tag, Button, Space, Tooltip } from '@/shared/ui/primitives';
import { EditOutlined, DeleteOutlined, EyeOutlined, LinkOutlined } from '@/shared/ui/icons';
import { formatDateTime, formatAdPrice } from '@/shared/utils/formatters';
import { useTranslation } from 'react-i18next';
import type { ColumnsType, TableProps } from '@/shared/ui/subpath-types';
import { publicRoomWantedUrl } from '@/shared/utils/publicLinks';

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'default',
  PUBLISHED: 'success',
  ARCHIVED: 'warning',
  REJECTED: 'error',
};

interface Props {
  data: any[];
  loading: boolean;
  sortField: string;
  sortDir: string;
  onSort: (field: string) => void;
  onView: (id: number) => void;
  onEdit: (id: number) => void;
  onDelete: (item: any) => void;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  selectedIds?: number[];
  onSelectionChange?: (ids: number[]) => void;
}

export default function RoomWantedTable({ data, loading, sortField, sortDir, onSort, onView, onEdit, onDelete, page, pageSize, total, onPageChange, selectedIds, onSelectionChange }: Props) {
  const { t } = useTranslation();

  const columns: ColumnsType<any> = [
    { title: t('common.id'), dataIndex: 'id', sorter: true, width: 80 },
    {
      title: t('common.title'), dataIndex: 'title', sorter: true, width: 200, ellipsis: true,
      render: (v: string) => v || '—',
    },
    {
      title: t('roomWanted.status'), dataIndex: 'status', sorter: true, width: 110,
      render: (v: string) => <Tag color={STATUS_COLORS[v] || 'default'}>{v}</Tag>,
    },
    {
      title: t('roomWanted.budget'), dataIndex: 'budget', sorter: true,
      render: (v: number | null) => formatAdPrice(v, t),
    },
    {
      title: t('roomWanted.user'), dataIndex: 'userName',
      render: (_: any, record: any) => record.userName ? `${record.userName} (#${record.userId})` : '—',
    },
    {
      title: t('common.city'), dataIndex: 'cityName',
      render: (v: string) => v || '—',
    },
    {
      title: t('roomWanted.featured'), dataIndex: 'isFeatured', sorter: true, width: 100,
      render: (v: boolean) => <Tag color={v ? 'purple' : 'default'}>{v ? t('common.yes') : t('common.no')}</Tag>,
    },
    {
      title: t('common.createdAt'), dataIndex: 'createdAt', sorter: true,
      render: (v: string) => formatDateTime(v),
    },
    {
      title: t('common.actions'), width: 160,
      render: (_: any, record: any) => {
        const publicUrl = publicRoomWantedUrl(record.id);
        return (
          <Space>
            <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => onView(record.id)} data-testid={`roomwanted-view-${record.id}`} />
            <span data-testid={`roomwanted-public-link-${record.id}`}>
              <Tooltip title={t('common.viewOnSite', { defaultValue: 'View on site' })}>
                <Button
                  type="text"
                  size="small"
                  icon={<LinkOutlined />}
                  aria-label={t('common.viewOnSite', { defaultValue: 'View on site' })}
                  onClick={() => window.open(publicUrl, '_blank', 'noopener,noreferrer')}
                />
              </Tooltip>
            </span>
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => onEdit(record.id)} data-testid={`roomwanted-edit-${record.id}`} />
            <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => onDelete(record)} data-testid={`roomwanted-delete-${record.id}`} />
          </Space>
        );
      },
    },
  ];

  const handleChange: TableProps['onChange'] = (_pagination, _filters, sorter: any, extra: any) => {
    // Only re-sort on an actual sort action — otherwise paginating after a sort
    // resets back to page 0 (handleSort calls setPage(0)).
    if (extra?.action === 'sort' && sorter?.field) onSort(sorter.field);
  };

  const rowSelection = onSelectionChange
    ? {
        selectedRowKeys: selectedIds ?? [],
        onChange: (keys: React.Key[]) => onSelectionChange(keys.map((k) => Number(k))),
      }
    : undefined;

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={data}
      loading={loading}
      onChange={handleChange}
      sortDirections={['ascend', 'descend']}
      showSorterTooltip={false}
      rowSelection={rowSelection}
      scroll={{ x: 1120 }}
      pagination={{
        current: page + 1,
        pageSize,
        total,
        showTotal: (total) => `${total} items`,
        showSizeChanger: false,
        onChange: (p) => onPageChange(p - 1),
      }}
    />
  );
}
